import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect, Route} from 'react-router-dom';

import {createUrlFromNutshellFilterObject} from 'nutshell-core';

import {deserializeDate} from 'nutshell-core/deserialize-date';
import {deserializeFieldNames} from 'nutshell-core/entities/deserialize-fields';
import {convertToArray} from 'nutshell-core/filter';
import {getLeadsSchema} from 'nutshell-core/leads';
import type {FilterObject} from 'nutshell-core/types';

import {addOffline} from 'shells/hoc/add-offline';

import type {NutshellState} from '../../../store';

import {FeatureGatePageWrapper} from '../../limits/feature-gates/feature-gate-page-wrapper';

import {addChartDataFetching} from '../charts/add-chart-data-fetching';
import {CardChart} from '../charts/card-chart';
import {DetailViewContainer} from '../leads-reports/detail-view-container';
import {getReportDefaultState, getReportDefaultFields} from '../report-constants';
import {getAxisConfig, getTickLabelFontStyles} from '../report-styles';
import type {LeadsReportKey, ReportURLParams} from '../report-types';
import * as actions from '../reports-actions';
import * as reportSelectors from '../reports-selectors';

import {getChannelIdFromName} from './helpers';

import styles from './attribution-report.module.css';

const ReportCardChart = addOffline(addChartDataFetching(CardChart));

type Props = {
    reportKey: LeadsReportKey;
    onParamChange: (arg1: FilterObject, arg2: boolean) => any;
    urlParams: ReportURLParams<'leads'>;
    location: any;
};

export function AttributionReport(props: Props) {
    const {reportKey} = props;
    const dispatch = useDispatch();
    const leadSchema = useSelector((state: NutshellState) => getLeadsSchema(state));
    const isFiltering = useSelector((state: NutshellState) =>
        reportSelectors.getIsFilterDrawerOpen(state)
    );

    const {search: defaultQuery} = getReportDefaultState('attribution');
    const reportParams = props.urlParams.report;
    const filters = props.urlParams.filter;

    React.useEffect(() => {
        const params = {
            ...reportParams,
            reportType: 'attribution',
            groupByField: 'channels',
            segment: 'channels',
        } as const;

        dispatch(
            actions.requestLeadsReportData({
                reportKey,
                reportParams: params,
                filters: convertToArray(convertFiltersForPeriod(filters, 'createdTime')),
            })
        );
    }, [reportKey, reportParams, filters, dispatch]);

    let queryString = location.search;
    if (!queryString) {
        queryString = defaultQuery;

        return <Redirect to={`${location.pathname}${queryString}`} />;
    }

    const listParams = props.urlParams.list || {};
    const selectedFields =
        // @ts-expect-error
        listParams.fields && listParams.fields.leads
            ? // @ts-expect-error
              deserializeFieldNames(listParams.fields.leads)
            : deserializeFieldNames(getReportDefaultFields('attribution'));

    const handleBarClick = (channelName: string, onlyWon?: boolean) => {
        const channelId = getChannelIdFromName(channelName);
        const urlFilters = props.urlParams.filter;

        // Keep existing filters and add a channel filter matching the bar that was clicked
        let newFilters = {
            ...urlFilters,
            // Date needs to be an object to work in createUrlFromNutshellFilterObject below
            period: deserializeDate(urlFilters.period),
            channels: {
                anyAll: 'any',
                data: [channelId],
            },
        };

        // For the win rate table, we only want to show won leads
        if (onlyWon) {
            // @ts-expect-error
            newFilters = {...newFilters, status: 'Won'};
        }

        const convertedFilters = convertFiltersForPeriod(
            newFilters,
            onlyWon ? 'closedTime' : 'createdTime'
        );

        // @ts-expect-error
        const filterString = createUrlFromNutshellFilterObject(convertedFilters, leadSchema);
        const leadsUrl = `/leads?fields[leads]=name,value,age,channels,sources,milestone,confidence,closedTime&${filterString}&sort=name&report[gap]=w&report[type]=createdTime&report[pivotFacet]=count&view=list&persist=false`;
        // Open the filtered leads list in a new tab
        window.open(leadsUrl, '_blank');
    };

    return NutClientConfig.hasAttributionReport ? (
        <Route path={'/reports/:report/:tab'}>
            {({match}) => {
                return (
                    <div
                        id='report-content-scroll-container'
                        className={isFiltering ? styles['container--narrow'] : styles.container}
                    >
                        <div className={styles['report-card-container']}>
                            <div className={styles['absolute-card-wrapper']}>
                                <ReportCardChart
                                    title='All leads by channel'
                                    reportParams={{
                                        facet: reportParams.facet,
                                        reportType: 'attribution',
                                        groupByField: 'channels',
                                        segment: 'channels',
                                    }}
                                    filters={convertToArray(
                                        convertFiltersForPeriod(
                                            props.urlParams.filter,
                                            'createdTime'
                                        )
                                    )}
                                    onBarClick={(data: {name: string}) => handleBarClick(data.name)}
                                    axisConfig={getAxisConfig(true)}
                                    tickLabelFontStyles={getTickLabelFontStyles(true)}
                                />
                            </div>
                        </div>
                        <div className={styles['report-card-container']}>
                            <div className={styles['absolute-card-wrapper']}>
                                <ReportCardChart
                                    title='Won leads by channel'
                                    reportParams={{
                                        facet: reportParams.facet,
                                        reportType: 'attribution',
                                        groupByField: 'channels',
                                        segment: 'outcomes',
                                    }}
                                    filters={convertToArray(
                                        convertFiltersForPeriod(
                                            props.urlParams.filter,
                                            'closedTime'
                                        )
                                    )}
                                    onBarClick={(data: {name: string}) =>
                                        handleBarClick(data.name, true)
                                    }
                                    axisConfig={getAxisConfig(true)}
                                    tickLabelFontStyles={{
                                        letterSpacing: -0.2,
                                        fontSize: 13,
                                    }}
                                    isPercentage={true}
                                />
                            </div>
                        </div>
                        <DetailViewContainer
                            reportKey={props.reportKey}
                            // @ts-expect-error
                            selectedTab={match.params.tab || 'details'}
                            location={props.location}
                            filters={convertToArray(
                                convertFiltersForPeriod(props.urlParams.filter, 'createdTime')
                            )}
                            facet={reportParams.facet}
                            segmentId='channels'
                            selectedPage={
                                // @ts-expect-error
                                listParams.page && Number(listParams.page)
                                    ? // @ts-expect-error
                                      Number(listParams.page)
                                    : 1
                            }
                            // @ts-expect-error
                            selectedSortField={listParams.sort}
                            selectedFields={selectedFields}
                            onParamChange={props.onParamChange}
                        />
                    </div>
                );
            }}
        </Route>
    ) : (
        <FeatureGatePageWrapper
            header='Uncover where your best leads come from'
            subHeader='Gain valuable insights into your marketing performance and see which channels are generating the best leads.'
            bullets={[
                'View new and won leads broken down by channel, helping you identify what’s driving results.',
                'Focus your digital marketing efforts on the channels that bring in the most wins.',
                'Easily segment and filter leads to track their performance over time and make smart adjustments.',
            ]}
            feature='attributionReport'
        />
    );
}

function convertFiltersForPeriod(filter: any | null | undefined, periodType: string): any {
    if (!filter) return null;

    return {
        ...filter,
        [periodType]: filter.period,
    };
}
